import Link from 'next/link'
import { Reveal } from '@/components/site/Reveal'
import { ordinal, type Article } from '@/lib/portfolio'

/**
 * The blog as an index: number, category and title on the left, the summary
 * and its date on the right.
 *
 * The same row is used on the home page and on the blog listing — the only
 * difference there is how many of them there are, which is the caller's
 * decision rather than this component's.
 */
export function ArticleRows({ articles }: { articles: Article[] }) {
    return (
        <div>
            {articles.map((article, index) => (
                <Reveal key={article.slug} delay={index * 40}>
                    <Link
                        href={article.url}
                        className="grid grid-cols-[44px_6fr_4fr] items-start gap-6 border-t border-hair px-gutter py-7 transition-[padding,background] duration-500 ease-design hover:bg-hover hover:pl-[46px] max-lg:grid-cols-1 max-lg:gap-3"
                    >
                        <span className="font-mono text-[11px] text-faint">{ordinal(index)}</span>
                        <span className="block">
                            <span className="mb-2.5 block font-mono text-[10.5px] tracking-[0.08em] text-accent">
                                {article.category}
                            </span>
                            <span className="block max-w-[20ch] text-[clamp(20px,2.4vw,30px)] leading-tight font-normal tracking-[-0.035em] text-balance">
                                {article.title}
                            </span>
                        </span>
                        <span className="block">
                            {/* Two lines: the lede is written for the article's
                             * own page, and at full length it turned a scannable
                             * index into a wall.
                             *
                             * The width is capped below what the column is at
                             * its narrowest — hover pushes the row 20px to the
                             * right — so the lines break at the same words
                             * whether or not the pointer is on the row. */}
                            <span className="line-clamp-2 max-w-[27rem] text-[15px] leading-relaxed text-pretty text-dim">
                                {article.intro}
                            </span>
                            <span className="mt-3 block font-mono text-[10.5px] text-faint">
                                {article.date}
                                {article.readingTime === '' ? '' : ` · ${article.readingTime}`}
                            </span>
                        </span>
                    </Link>
                </Reveal>
            ))}
        </div>
    )
}
